/**
 * Shared “section scrolled into view” helper for reviews and recommendations: UGC widgets and CMS
 * blocks often mount after first paint or after consent, so we both scan once and watch `body` for new
 * selector matches instead of assuming nodes exist at attach time.
 *
 * Plugin coverage: none by itself — callers pass selectors; used by `ReviewsReachedStrategy` and
 * `RecommendationsReachedStrategy` (see those classes for Trustpilot, cross-selling, etc.).
 */
export function bindSectionReachedObserver(options: {
  document: Document;
  selectors: readonly string[];
  threshold?: number;
  onReached: () => void;
}): () => void {
  const threshold = options.threshold ?? 0.1;
  const observed = new Set<Element>();
  const observers: IntersectionObserver[] = [];
  const disposers: (() => void)[] = [];

  const attachNew = (): void => {
    for (const sel of options.selectors) {
      let list: NodeListOf<Element>;
      try {
        list = options.document.querySelectorAll(sel);
      } catch {
        continue;
      }
      list.forEach((el) => {
        if (observed.has(el)) {
          return;
        }
        observed.add(el);
        const io = new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (!entry.isIntersecting) {
                continue;
              }
              options.onReached();
            }
          },
          { threshold },
        );
        io.observe(el);
        observers.push(io);
      });
    }
  };

  attachNew();

  const body = options.document.body;
  if (body) {
    const mo = new MutationObserver(() => {
      attachNew();
    });
    mo.observe(body, { childList: true, subtree: true });
    disposers.push(() => mo.disconnect());
  }

  disposers.push(() => {
    observers.forEach((o) => o.disconnect());
    observers.length = 0;
    observed.clear();
  });

  return () => {
    disposers.forEach((d) => d());
    disposers.length = 0;
  };
}
